import { useEffect } from "react";
import { SITE_URL, ROUTES, LANGS } from "../lib/routes";

const OG_LOCALES = { en: "en_US", es: "es_ES", fr: "fr_FR", de: "de_DE", it: "it_IT", pt: "pt_PT" };

function upsertMeta(attr, key, content) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { if (tag && tag.dataset.seo) tag.remove(); return; }
  if (!tag) { tag = document.createElement("meta"); tag.setAttribute(attr, key); tag.dataset.seo = "1"; document.head.appendChild(tag); }
  tag.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let tag = document.head.querySelector(sel);
  if (!href) { if (tag) tag.remove(); return; }
  if (!tag) { tag = document.createElement("link"); tag.setAttribute("rel", rel); if (hreflang) tag.setAttribute("hreflang", hreflang); document.head.appendChild(tag); }
  tag.setAttribute("href", href);
}

function setJsonLd(blocks) {
  document.head.querySelectorAll('script[data-seo-ld]').forEach((s) => s.remove());
  (blocks || []).forEach((b) => {
    if (!b) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.seoLd = "1";
    s.textContent = JSON.stringify(b);
    document.head.appendChild(s);
  });
}

// Central SEO manager. pageKey drives canonical + reciprocal hreflang from ROUTES.
// For dynamic pages (blog articles) pass explicit `path` and `alternates` [{lang, path}].
export default function Seo({ pageKey, lang = "en", title, description, image, type = "website", path, alternates, jsonLd, noindex = false }) {
  useEffect(() => {
    const route = pageKey ? ROUTES[pageKey] : null;
    const curPath = path || (route ? route[lang] : window.location.pathname);
    const canonical = `${SITE_URL}${curPath === "/" ? "/" : curPath}`;

    document.title = title || "Equestrian Partners";
    document.documentElement.lang = lang;
    upsertMeta("name", "description", description || "");

    const isPreview = window.location.hostname !== "equestrianpartners.net";
    upsertMeta("name", "robots", noindex || isPreview ? "noindex, nofollow" : "index, follow");

    upsertLink("canonical", canonical);
    const alts = alternates || (route ? LANGS.map((l) => ({ lang: l, path: route[l] })).filter((a) => a.path) : []);
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
    alts.forEach((a) => upsertLink("alternate", `${SITE_URL}${a.path}`, a.lang));
    const xd = alts.find((a) => a.lang === "en") || alts[0];
    if (xd) upsertLink("alternate", `${SITE_URL}${xd.path}`, "x-default");

    const img = image ? (image.startsWith("http") ? image : `${SITE_URL}${image}`) : `${SITE_URL}/og-image-v2.jpg`;
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "Equestrian Partners");
    upsertMeta("property", "og:locale", OG_LOCALES[lang] || "en_US");
    upsertMeta("property", "og:locale:alternate", lang === "en" ? "es_ES" : "en_US");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:title", title || "Equestrian Partners");
    upsertMeta("property", "og:description", description || "");
    const isDefaultOg = img.endsWith("/og-image-v2.jpg");
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:image:secure_url", img);
    upsertMeta("property", "og:image:width", isDefaultOg ? "1200" : "1536");
    upsertMeta("property", "og:image:height", isDefaultOg ? "630" : "1024");
    upsertMeta("property", "og:image:alt", title || "Equestrian Partners");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title || "Equestrian Partners");
    upsertMeta("name", "twitter:description", description || "");
    upsertMeta("name", "twitter:image", img);

    setJsonLd(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []);
    return () => setJsonLd([]);
  }, [pageKey, lang, title, description, image, type, path, alternates, jsonLd, noindex]);
  return null;
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.name, item: `${SITE_URL}${it.path}`,
    })),
  };
}

export const ORG_ID = `${SITE_URL}/#organization`;

export function orgRef() {
  return { "@id": ORG_ID };
}
